// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import { Moves } from '@gql/base-types';

import { getFormattedVehicleFromMove } from '@services/moveService';
import { formatPhone } from '@services/formatService';

import { Button, Typography } from '@mui/material';
import { BiEdit } from 'react-icons/bi';

import Table from '@components/TableComponents/Table';
import Column from '@components/TableComponents/Table';
import MoveIdBadge from '@components/Badges/MoveIdBadge';
import MoveTypeBadge from '@components/Badges/MoveTypeBadge';
import MoveStatusBadge from '@components/Badges/MoveStatusBadge';

import { Metric, useMissingRefReport } from '@features/missingRefReport/providers/MissingRefReportProvider';
import { movePath } from '@features/products/productRoutes';

// Map table column names to database field names
const columnToDbFieldMap: Record<string, string> = {
  market: 'customer_id',
  payer: 'payer_id',
  move_id: 'id',
  type: 'consumer_type',
  consumer: 'consumer_name',
  time_requested: 'ready_by',
  time_completed: 'delivery_successful',
  time_canceled: 'canceled_at',
  cancel_reason: 'cancel_reason',
  reason_for_visit: 'config',
  status: 'status',
  vin: 'vehicle_vin',
  vehicle: 'vehicle_year',
  address: 'lane_id',
};

// Reverse mapping: database field names to column names
const dbFieldToColumnMap: Record<string, string> = Object.entries(columnToDbFieldMap).reduce(
  (acc, [columnName, dbField]) => {
    acc[dbField] = columnName;
    return acc;
  },
  {} as Record<string, string>
);

const formatTime = (time: string) => {
  if (time && typeof time === `string`) {
    let newTime = dayjs(time).format(`M/D/YYYY h:mm A z`);
    newTime = newTime.replace(/\s/g, '\xa0');
    return newTime;
  }
  return null;
};

const statusOrder = [
  ``,
  `dispatched`,
  `pickup started`,
  `pickup arrived`,
  `pickup successful`,
  `delivery started`,
  `delivery arrived`,
  `delivery successful`,
];

// TYPES ---------------------------------------------------------------- //

interface MissingRefReportTableProps {
  moves: Moves[];
  moveCount: number;
  metric: Metric;
  isAdmin: boolean;
  // Only the count is read, to decide whether the payer column shows.
  payersArray: unknown[];
  onAssignRef: (input?: { move: Moves }) => void;
}

// COMPONENT ---------------------------------------------------------------- //

const MissingRefReportTable = ({
  moves,
  moveCount,
  metric,
  isAdmin,
  payersArray,
  onAssignRef,
}: MissingRefReportTableProps) => {
  const navigate = useNavigate();

  const { orderBy, order, setOrder, setOrderBy, setOffset } = useMissingRefReport();

  // Convert DB field name to column name for Table component
  const tableOrderBy = dbFieldToColumnMap[orderBy] || orderBy;

  // Handle sort changes from Table component
  const handleOrderChange = (newOrder: 'asc' | 'desc' | false) => {
    if (newOrder === false) return; // Skip if false
    setOrder(newOrder);
    setOffset(0); // Reset to first page when sorting changes
  };

  const handleOrderByChange = (columnName: string) => {
    // Map column name to database field name
    const dbField = columnToDbFieldMap[columnName] || columnName;
    setOrderBy(dbField);
    setOffset(0); // Reset to first page when sorting changes
  };

  const isReasonPresent = React.useMemo(() => {
    return moves.some(move => move?.config?.moveInput?.reason_for_visit);
  }, [moves]);

  const getType = (move: Moves) => {
    const { consumer_type } = move;

    if (consumer_type === 'customer' && move?.consumer_at_pickup === 1) return 'Pickup';
    if (consumer_type === 'customer' && move?.consumer_at_pickup === 0) return 'Return';
    if (consumer_type === 'loaner') return 'Loaner';
    return 'Operational';
  };

  const getStatusOrder = (move: Moves) => {
    const { cancel_status, status } = move;
    if (cancel_status) return `0-${move?.ready_by || move?.id}`;
    return `${statusOrder.indexOf(status || ``) + 1}-${move?.ready_by || move?.id}`;
  };

  return (
    <Table
      data={moves}
      order={order}
      orderBy={tableOrderBy}
      onOrderChange={handleOrderChange}
      onOrderByChange={handleOrderByChange}
      tableAriaLabel='moves'
      stickyHeader={true}
      rowHref={row => movePath('id' in row ? (row as Moves).id : undefined)}
      usePagination={true}
      hidePagination={true}
      totalRecordsCount={moveCount}
    >
      <Column
        name='actions'
        value={undefined}
        renderer={(move: Moves) => (
          <Button
            size='small'
            startIcon={<BiEdit size={16} />}
            color='primary'
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              onAssignRef({ move });
            }}
          >
            Assign
          </Button>
        )}
        style={{ whiteSpace: 'nowrap' }}
      />

      {isAdmin ? (
        <Column
          name='market'
          value={(move: Moves) => move?.customer?.name}
          renderer={(move: Moves) => move?.customer?.name || '-'}
          label='Market'
          style={{ minWidth: 192 }}
        />
      ) : null}

      {payersArray && payersArray.length > 0 ? (
        <Column
          name='payer'
          value={(move: Moves) => move?.payer_id || move?.customer?.name}
          renderer={(move: Moves) => move?.payer_id || move?.customer?.name || '-'}
          label='Payer'
          style={{ minWidth: 192 }}
        />
      ) : null}

      <Column
        name='move_id'
        value={(move: Moves) => move?.id}
        renderer={(move: Moves) => <MoveIdBadge move={move} />}
        label='Move ID'
        tooltip={`HopDrive's internal ID`}
        style={{ minWidth: 104 }}
      />

      <Column
        name='type'
        value={(move: Moves) => getType(move)}
        renderer={(move: Moves) => <MoveTypeBadge move={move} />}
        label='Type'
        style={{ minWidth: 96 }}
      />

      <Column
        name='consumer'
        value={(move: Moves) => `${move?.consumer_name} ${formatPhone(move?.consumer_phone)}`}
        renderer={(move: Moves) => (
          <div>
            {move?.consumer_name && (
              <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                {move?.consumer_name}
              </Typography>
            )}
            {move?.consumer_phone && (
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                {formatPhone(move?.consumer_phone)}
              </Typography>
            )}
          </div>
        )}
        label='Customer'
        style={{ minWidth: 144 }}
      />

      <Column
        name='time_requested'
        value={(move: Moves) => move?.ready_by}
        renderer={(move: Moves) => formatTime(move?.ready_by || '') || '-'}
        label='Time Requested'
        style={{ minWidth: 144 }}
      />

      {metric === 'completed' ? (
        <Column
          name='time_completed'
          value={(move: Moves) => move?.delivery_successful}
          renderer={(move: Moves) => formatTime(move?.delivery_successful || '') || '-'}
          label='Time Completed'
          style={{ minWidth: 144 }}
        />
      ) : null}

      {metric === 'canceled' ? (
        <Column
          name='time_canceled'
          value={(move: Moves) => move?.canceled_at}
          renderer={(move: Moves) => formatTime(move?.canceled_at || '') || '-'}
          label='Time Canceled'
          style={{ minWidth: 144 }}
        />
      ) : null}

      {metric === 'canceled' ? (
        <Column
          name='cancel_reason'
          value={(move: Moves) => move?.cancel_reason}
          renderer={(move: Moves) => move?.cancel_reason || '-'}
          label='Cancel Reason'
          style={{ minWidth: 160 }}
        />
      ) : null}

      {/* TODO: THIS SHOULD ONLY BE SHOWN FOR CERTAIN CUSTOMERS THAT HAVE THIS CONFIGURED */}
      {isReasonPresent ? (
        <Column
          name='reason_for_visit'
          value={(move: Moves) => move?.config?.moveInput?.reason_for_visit}
          renderer={(move: Moves) => move?.config?.moveInput?.reason_for_visit || '-'}
          label='Reason for Visit'
          style={{ minWidth: 192 }}
        />
      ) : null}

      <Column
        name='status'
        value={(move: Moves) => getStatusOrder(move)}
        renderer={(move: Moves) => <MoveStatusBadge move={move} />}
        label='Status'
        style={{ minWidth: 120 }}
      />

      <Column
        name='vin'
        value={(move: Moves) => move?.vehicle_vin}
        renderer={(move: Moves) => move?.vehicle_vin || '-'}
        label='VIN'
        style={{ minWidth: 96 }}
      />

      <Column
        name='vehicle'
        value={(move: Moves) => getFormattedVehicleFromMove(move, true, false)}
        label='Vehicle'
        style={{ minWidth: 192 }}
      />

      <Column
        name='address'
        value={(move: Moves) => {
          // Split on "USA" or "United States" with regex to save some space
          const splitAddress = (address?: string | null) => address?.split(/, USA|, United States/)?.[0] || '';

          // If no address, return '-'
          if (!move?.lane?.pickup?.address && !move?.lane?.delivery?.address) return '-';
          if (move?.consumer_at_pickup === 1) return splitAddress(move?.lane?.pickup?.address);
          if (move?.consumer_at_pickup === 0) return splitAddress(move?.lane?.delivery?.address);
          return '-';
        }}
        label='Address'
        style={{ minWidth: 192 }}
      />
    </Table>
  );
};

// EXPORT ---------------------------------------------------------------- //

// Memoize the component to prevent unnecessary re-renders
// Only re-render when moves data or other props change
export default React.memo(MissingRefReportTable, (prevProps, nextProps) => {
  // Check if moves array has actually changed
  const movesEqual =
    prevProps.moves.length === nextProps.moves.length &&
    prevProps.moves.every((_, index) => {
      const prevMove = prevProps.moves[index];
      const nextMove = nextProps.moves[index];
      // Compare key fields that would affect the table display
      return (
        prevMove?.id === nextMove?.id &&
        prevMove?.status === nextMove?.status &&
        prevMove?.driver_id === nextMove?.driver_id &&
        prevMove?.cancel_status === nextMove?.cancel_status &&
        prevMove?.consumer_pickup === nextMove?.consumer_pickup
      );
    });

  // Check if other props have changed
  const otherPropsEqual =
    prevProps.metric === nextProps.metric &&
    prevProps.isAdmin === nextProps.isAdmin &&
    prevProps.moveCount === nextProps.moveCount &&
    JSON.stringify(prevProps.payersArray) === JSON.stringify(nextProps.payersArray);

  // Return true if props are equal (skip re-render), false if different (re-render)
  return movesEqual && otherPropsEqual;
});
